import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cloud,
  Copy,
  Check,
  RefreshCw,
  AlertTriangle,
  Plug,
} from "lucide-react";
import { toast } from "react-toastify";
import { integrationAPI } from "../../services/api";
import Button from "../UI/Button";
import SectionCard from "../UI/SectionCard";
import { Modal } from "../UI/Modal";
import { FormField, Input } from "../UI/FormField";
import LoadingSpinner from "../UI/LoadingSpinner";
import ConfirmationDialog from "../modals/ConfirmationDialog";
import { ToggleSwitch } from "../shipments/ToggleSwitch";
import type {
  DynamicsConnection,
  DynamicsConnectionStatus,
} from "../../types/orders";

const STATUS_BADGE: Record<DynamicsConnectionStatus, string> = {
  pending: "bg-gray-100 text-gray-700",
  connected: "bg-green-100 text-green-800",
  needs_reauth: "bg-red-100 text-red-700",
  disabled: "bg-amber-100 text-amber-800",
};

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("et-EE") : null;

type BcCompanyOption = { bcCompanyId: string; name: string; enabled: boolean };

const DynamicsConnectionCard: React.FC = () => {
  const { t } = useTranslation("orders");

  const [connection, setConnection] = useState<DynamicsConnection | null>(null);
  const [consentUrl, setConsentUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [loading, setLoading] = useState(true);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [environment, setEnvironment] = useState("Production");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState<"link" | "clientId" | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const [companies, setCompanies] = useState<BcCompanyOption[] | null>(null);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  const load = async () => {
    try {
      const res = await integrationAPI.getDynamics();
      const data = res.data.data;
      setConnection(data.connection);
      setConsentUrl(data.consentUrl);
      setClientId(data.clientId);
      if (data.connection) {
        setTenantId(data.connection.tenantId);
        setEnvironment(data.connection.environment);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to load integration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyText = async (text: string, which: "link" | "clientId") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleSaveConnection = async () => {
    setSaving(true);
    try {
      const res = await integrationAPI.saveDynamics({
        tenantId: tenantId.trim(),
        environment: environment.trim() || "Production",
      });
      setConnection(res.data.data.connection);
      setConsentUrl(res.data.data.consentUrl);
      setCompanies(null);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to save connection");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await integrationAPI.testDynamics();
      setConnection(res.data.data.connection);
      setCompanies(
        res.data.data.companies.map((c: any) => ({
          ...c,
          enabled:
            connection?.selectedCompanies?.some(
              (s) => s.bcCompanyId === c.bcCompanyId && s.enabled,
            ) ?? false,
        })),
      );
      toast.success(t("integrations.testSuccess"));
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("integrations.syncFailed"));
      await load();
    } finally {
      setTesting(false);
    }
  };

  const handleLoadCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const res = await integrationAPI.listDynamicsCompanies();
      setCompanies(
        res.data.data.companies.map((c: any) => ({
          ...c,
          enabled:
            connection?.selectedCompanies?.some(
              (s) => s.bcCompanyId === c.bcCompanyId && s.enabled,
            ) ?? false,
        })),
      );
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("integrations.syncFailed"));
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleSaveCompanies = async () => {
    if (!companies) return;
    setSaving(true);
    try {
      const res = await integrationAPI.saveDynamicsCompanies(
        companies.filter((c) => c.enabled),
      );
      setConnection(res.data.data.connection);
      toast.success(t("integrations.testSuccess"));
      setWizardOpen(false);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to save companies");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!connection) return;
    const enable = connection.status !== "connected";
    try {
      const res = await integrationAPI.setDynamicsEnabled(enable);
      setConnection(res.data.data.connection);
      toast.success(
        enable ? t("integrations.enableSync") : t("integrations.disableSync"),
      );
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to toggle sync");
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await integrationAPI.syncDynamicsNow();
      const s = res.data.data.summary;
      toast.success(
        t("integrations.syncDone", {
          created: s.created ?? 0,
          updated: s.updated ?? 0,
        }),
      );
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || t("integrations.syncFailed"));
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await integrationAPI.disconnectDynamics();
      setConnection(null);
      setCompanies(null);
      setTenantId("");
      setEnvironment("Production");
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to disconnect");
    }
  };

  const statusLabel = (s: DynamicsConnectionStatus) =>
    ({
      pending: t("integrations.statusPending"),
      connected: t("integrations.statusConnected"),
      needs_reauth: t("integrations.statusNeedsReauth"),
      disabled: t("integrations.statusDisabled"),
    })[s];

  if (loading) {
    return (
      <SectionCard icon={Cloud} title={t("integrations.dynamicsTitle")}>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard
        icon={Cloud}
        title={t("integrations.dynamicsTitle")}
        actions={
          connection ? (
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[connection.status]}`}
            >
              {statusLabel(connection.status)}
            </span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
              {t("integrations.notConnected")}
            </span>
          )
        }
      >
        <p className="text-[13px] text-[#8A9590] mb-3">
          {t("integrations.dynamicsSubtitle")}
        </p>

        {!connection ? (
          <Button
            variant="primary"
            size="sm"
            icon={<Plug className="h-4 w-4" />}
            onClick={() => setWizardOpen(true)}
          >
            {t("integrations.connect")}
          </Button>
        ) : (
          <div className="space-y-3">
            {connection.status === "needs_reauth" && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-[13px] rounded-md px-3 py-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  {t("integrations.reauthHint")}
                  {connection.lastError && (
                    <div className="mt-1 text-[11px] opacity-75 break-all">
                      {connection.lastError}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-[13px]">
              <div>
                <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide">
                  {t("integrations.tenantId")}
                </div>
                <div className="font-mono text-[12px]">
                  {connection.tenantId.slice(0, 8)}…
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide">
                  {t("integrations.environment")}
                </div>
                <div>{connection.environment}</div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide">
                  {t("integrations.companiesTitle")}
                </div>
                <div>
                  {connection.selectedCompanies.filter((c) => c.enabled)
                    .length > 0
                    ? connection.selectedCompanies
                        .filter((c) => c.enabled)
                        .map((c) => c.name || c.bcCompanyId)
                        .join(", ")
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide">
                  {t("integrations.lastSync")}
                </div>
                <div>
                  {fmtDateTime(connection.lastSyncAt) ??
                    t("integrations.never")}
                  {connection.lastSyncStatus && (
                    <span className="ml-1 text-[11px] text-[#8A9590]">
                      ({connection.lastSyncStatus})
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <ToggleSwitch
                checked={connection.status === "connected"}
                onChange={handleToggleEnabled}
                label={
                  connection.status === "connected"
                    ? t("integrations.enableSync")
                    : t("integrations.disableSync")
                }
                disabled={connection.status === "needs_reauth"}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncNow}
                isLoading={syncing}
                disabled={connection.status !== "connected"}
                icon={<RefreshCw className="h-4 w-4" />}
              >
                {syncing ? t("integrations.syncing") : t("integrations.syncNow")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWizardOpen(true)}
              >
                {t("integrations.detailsTitle")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setDisconnectOpen(true)}
              >
                {t("integrations.disconnect")}
              </Button>
            </div>

            {connection.syncErrors?.length > 0 && (
              <div className="pt-1">
                <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide mb-1">
                  {t("integrations.recentErrors")}
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {[...connection.syncErrors].reverse().map((err, i) => (
                    <div
                      key={i}
                      className="text-[12px] text-red-700 bg-red-50 rounded px-2 py-1 break-all"
                    >
                      <span className="text-[#8A9590] mr-1">
                        {fmtDateTime(err.at)}
                        {err.resource ? ` · ${err.resource}` : ""}:
                      </span>
                      {err.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Connect / settings wizard */}
      <Modal
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        title={t("integrations.dynamicsTitle")}
        size="lg"
      >
        <div className="p-4 space-y-5 overflow-y-auto">
          {/* Step 1: admin consent */}
          <div>
            <h4 className="text-[13px] font-semibold text-[#0B0F0E] mb-1">
              {t("integrations.consentTitle")}
            </h4>
            <p className="text-[13px] text-[#8A9590] mb-2">
              {t("integrations.consentBody")}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] bg-[#F5F4F0] rounded px-2 py-1.5 break-all">
                {consentUrl}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyText(consentUrl, "link")}
                icon={
                  copied === "link" ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )
                }
              >
                {copied === "link"
                  ? t("integrations.copied")
                  : t("integrations.copyLink")}
              </Button>
            </div>
          </div>

          {/* Step 2: BC registration */}
          <div>
            <h4 className="text-[13px] font-semibold text-[#0B0F0E] mb-1">
              {t("integrations.bcSetupTitle")}
            </h4>
            <ol className="list-decimal pl-5 text-[13px] text-[#8A9590] space-y-1">
              <li>{t("integrations.bcStep1")}</li>
              <li>{t("integrations.bcStep2")}</li>
              <li>{t("integrations.bcStep3")}</li>
            </ol>
            <div className="mt-2">
              <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide mb-1">
                {t("integrations.clientIdLabel")}
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] bg-[#F5F4F0] rounded px-2 py-1.5 break-all">
                  {clientId || "—"}
                </code>
                {clientId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyText(clientId, "clientId")}
                    icon={
                      copied === "clientId" ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )
                    }
                  />
                )}
              </div>
            </div>
          </div>

          {/* Step 3: details */}
          <div>
            <h4 className="text-[13px] font-semibold text-[#0B0F0E] mb-2">
              {t("integrations.detailsTitle")}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField
                label={t("integrations.tenantId")}
                required
                hint={t("integrations.tenantIdHint")}
              >
                <Input
                  size="sm"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </FormField>
              <FormField
                label={t("integrations.environment")}
                hint={t("integrations.environmentHint")}
              >
                <Input
                  size="sm"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  placeholder="Production"
                />
              </FormField>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveConnection}
                isLoading={saving}
                disabled={!tenantId.trim()}
              >
                {t("integrations.saveConnection")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleTest}
                isLoading={testing}
                disabled={!connection}
              >
                {testing
                  ? t("integrations.testing")
                  : t("integrations.testConnection")}
              </Button>
            </div>
          </div>

          {/* Step 4: companies */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-[13px] font-semibold text-[#0B0F0E]">
                {t("integrations.companiesTitle")}
              </h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLoadCompanies}
                isLoading={companiesLoading}
                disabled={!connection || connection.status === "pending"}
              >
                {t("integrations.loadCompanies")}
              </Button>
            </div>
            <p className="text-[13px] text-[#8A9590] mb-2">
              {t("integrations.companiesHint")}
            </p>
            {companies === null ? null : companies.length === 0 ? (
              <p className="text-[13px] text-[#8A9590]">
                {t("integrations.companiesEmpty")}
              </p>
            ) : (
              <div className="space-y-1.5">
                {companies.map((c) => (
                  <label
                    key={c.bcCompanyId}
                    className="flex items-center gap-2 text-[13px] cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={c.enabled}
                      onChange={() =>
                        setCompanies((prev) =>
                          (prev ?? []).map((x) =>
                            x.bcCompanyId === c.bcCompanyId
                              ? { ...x, enabled: !x.enabled }
                              : x,
                          ),
                        )
                      }
                      className="h-4 w-4 accent-[#C97A3C]"
                    />
                    {c.name || c.bcCompanyId}
                  </label>
                ))}
                <div className="pt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveCompanies}
                    isLoading={saving}
                    disabled={!companies.some((c) => c.enabled)}
                  >
                    {t("integrations.companiesSave")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmationDialog
        isOpen={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={handleDisconnect}
        title={t("integrations.disconnectTitle")}
        message={t("integrations.disconnectMessage")}
        confirmText={t("integrations.disconnect")}
        type="danger"
      />
    </>
  );
};

export default DynamicsConnectionCard;
